/* FILE: ./frontend/src/features/canada/pages/SupplierPublicProfile.jsx */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet'; // ⚡ ADDED: For dynamic SEO and Social Sharing tags

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

export default function SupplierPublicProfile() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const { user } = useAuth();

  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Pass caller_id so backend applies 100% blind masking except for Owner and Admin
        const supRes = await axios.get(`${API_BASE}/suppliers/${id}${user ? `?caller_id=${user.id}` : ''}`);
        setSupplier(supRes.data);

        // Pass caller_id to unmask the Supplier details attached to the Products
        const prodRes = await axios.get(`${API_BASE}/products?supplier_id=${id}${user ? `&caller_id=${user.id}` : ''}`);
        setProducts(prodRes.data || []);
        
      } catch (err) {
        console.error("Profile Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, user]);

  if (loading) return <div className="p-12 text-center">{t('profile.loading')}</div>;

  if (!supplier) return <div className="p-12 text-center text-red-600">{t('profile.not_found')}</div>;

  const isOwner = user && supplier.user_id && String(user.id) === String(supplier.user_id);

  // ⚡ ARRAY SAFETY
  const categories = Array.isArray(supplier.categories) ? supplier.categories : [];
  const capabilities = Array.isArray(supplier.capabilities) ? supplier.capabilities : 
                       (typeof supplier.capabilities === 'string' ? [supplier.capabilities] : []);

  // ⚡ SEO FIX: Clean up the description for the meta tags (max 155 chars for Google)
  const metaDescription = supplier.description 
      ? (supplier.description.length > 150 ? `${supplier.description.substring(0, 150)}...` : supplier.description) 
      : `View ${supplier.company_name}'s verified supplier profile, capabilities, and product catalog on TariffBeater.`;

  // ⚡ OPTION A FOR RFQ: Route directly to /rfq/new with preselectedSupplier
  const handleRequestQuote = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate('/rfq/new', {
      state: {
        preselectedSupplier: supplier
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      
      {/* ⚡ SEO FIX: Dynamic Title, Meta, and OpenGraph tags */}
      <Helmet>
          <title>{supplier.company_name} | TariffBeater</title>
          <meta name="description" content={metaDescription} />
          
          {/* Social Sharing / OpenGraph */}
          <meta property="og:title" content={`${supplier.company_name} | Verified Canadian Fabricator`} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:type" content="profile" />
          <meta property="og:url" content={`https://www.tariffbeater.com/suppliers/${id}`} />
          {supplier.logo_url ? (
              <meta property="og:image" content={supplier.logo_url} />
          ) : (
              <meta property="og:image" content="https://www.tariffbeater.com/og-image.png" />
          )}
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/directory')} className="mb-6 text-gray-500 hover:text-blue-600 flex items-center gap-1 font-bold">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
            {t('profile.back_dir')}
        </button>

        {/* HEADER CARD */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start gap-6">
                <div className="flex items-center gap-6">
                    {supplier.logo_url ? (
                        <div className="w-20 h-20 rounded-xl border border-gray-200 overflow-hidden shrink-0 shadow-sm">
                            <img src={supplier.logo_url} alt={`${supplier.company_name} corporate logo`} className="w-full h-full object-cover" />
                        </div>
                    ) : (
                        <div className="w-20 h-20 bg-blue-600 rounded-xl flex items-center justify-center text-white text-3xl font-bold shrink-0 shadow-sm">
                            {supplier.company_name?.charAt(0)}
                        </div>
                    )}
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{supplier.company_name}</h1>
                        <div className="text-gray-500 mt-2 flex flex-wrap items-center gap-3">
                            <span className="flex items-center">📍 {supplier.city}, {supplier.province}</span>
                            {supplier.is_verified && (
                                <span className="bg-green-100 text-green-800 border border-green-200 text-xs px-2.5 py-1 rounded-full font-bold flex items-center">
                                    ✓ {t('profile.verified')}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {!isOwner && (
                    <button 
                        onClick={handleRequestQuote}
                        className="bg-blue-600 text-white px-8 py-3.5 rounded-lg font-bold shadow hover:bg-blue-700 hover:shadow-md transition-all shrink-0 w-full md:w-auto"
                    >
                        {t('supplier_card.req_quote', 'Request Quote')}
                    </button>
                )}
            </div>
            
            <div className="mt-8 grid md:grid-cols-3 gap-8 border-t border-gray-100 pt-8">
                <div className="md:col-span-2">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">{t('profile.about')}</h3>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{supplier.description || t('profile.no_desc')}</p>
                    
                    {/* ⚡ MASTERFORMAT CATEGORIES */}
                    {categories.length > 0 && (
                        <div className="mt-8">
                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">MasterFormat Categories</h3>
                            <div className="flex flex-wrap gap-2">
                                {categories.map((cat, i) => (
                                    <span key={i} className="bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1.5 rounded-lg text-sm font-bold shadow-sm">
                                        {cat}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mt-8 mb-3">{t('profile.capabilities')}</h3>
                    {capabilities.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {capabilities.map((cap, i) => (
                               <span key={i} className="bg-gray-50 text-gray-700 border border-gray-200 px-3 py-1.5 rounded-md text-sm font-medium">{cap}</span>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-400 italic">No specific capabilities listed.</p>
                    )}
                </div>
       
                <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl h-fit shadow-inner">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">{t('profile.quick_facts')}</h3>
                    <ul className="space-y-4 text-sm text-gray-600">
                        <li className="flex justify-between items-center border-b border-gray-200 pb-2">
                            <span>{t('profile.established')}</span> 
                            <span className="font-bold text-gray-900">{supplier.year_established || 'N/A'}</span>
                        </li>
                        <li className="flex justify-between items-center border-b border-gray-200 pb-2">
                            <span>{t('profile.employees')}</span> 
                            <span className="font-bold text-gray-900">{supplier.employees_count || t('profile.na')}</span>
                        </li>
                        <li className="flex justify-between items-center border-b border-gray-200 pb-2">
                            <span>{t('profile.compliance')}</span> 
                            <span className={`font-bold px-2 py-0.5 rounded text-xs ${
                                supplier.compliance_tier?.includes('Tier 1') ? 'bg-blue-100 text-blue-800' :
                                supplier.compliance_tier?.includes('Tier 2') ? 'bg-amber-100 text-amber-800' :
                                'bg-gray-200 text-gray-800'
                            }`}>
                                {supplier.compliance_tier || 'Unverified'}
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        {/* PRODUCTS GRID */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.parts_catalog')} ({products.length})</h2>
        {products.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200 text-gray-500 shadow-sm">
                <span className="text-4xl block mb-3">📦</span>
                {t('profile.no_products')}
            </div>
        ) : (
            <div className="grid md:grid-cols-3 gap-6">
                {products.map(product => (
                    <div 
                        key={product.id} 
                        className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer group flex flex-col h-full"
                        onClick={() => navigate(`/products/${product.id}`)}
                    >
                        <div className="flex justify-between items-start mb-3">
                            <span className="bg-blue-50 border border-blue-100 text-blue-700 text-xs font-mono px-2 py-1 rounded shadow-sm">
                                {t('profile.hs')} {product.hs_code}
                            </span>
                            <span className="text-green-600 text-[10px] uppercase tracking-wider font-bold">
                                {product.stock_status?.replace('_', ' ')}
                            </span>
                        </div>
                        <h3 className="font-bold text-lg text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">{product.name}</h3>
                        <p className="text-gray-500 text-sm mb-4 line-clamp-2 flex-grow">{product.description}</p>
                        
                        <div className="flex justify-between items-center pt-4 border-t border-gray-100 mt-auto">
                            <span className="font-extrabold text-lg text-gray-900">${product.price_cad}</span>
                            <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded">
                                {t('profile.min')} {product.moq} {product.uom}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>
   </div>
  );
}